import "./StainOrderDialog.css";

import { Case, LabOrder } from "pwapi";

import { cancelLabOrder } from "shared/actions/labOrdersActions";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import selectLabStains from "shared/selectors/selectLabStains";
import selectSortedCaseDetail from "shared/selectors/selectSortedCaseDetail";

import { useAppDispatch, useAppSelector } from "store/hooks";

import StainInputGroup from "./dialog-inputs/StainInputGroup";
import Sidebar from "./Sidebar";
import StainOrderGroup from "./StainOrderDialog/StainOrderGroup";

interface StainOrderDialogProps {
  slideDetailObject: SlideDetailState;
  setDialog?: Function;
}

export type ActiveLabOrders = {
  [key: string]: LabOrder[];
};

export type BlockDetails = {
  specimen?: number;
  block?: string | null;
  slide: number;
};

const StainOrderDialog = ({
  slideDetailObject,
  setDialog,
}: StainOrderDialogProps) => {
  const dispatch = useAppDispatch();
  const sortedCase = useAppSelector(selectSortedCaseDetail);

  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);
  const availableLabStains = useAppSelector((state) =>
    selectLabStains(state, sortedCase.accessioning_lab?.id)
  );

  const { blocks, slides } = sortedCase;
  const filteredLabOrders = (sortedCase.lab_orders || []).filter(
    (labOrder) =>
      !labOrder.cancelled_at &&
      !(
        (sortedCase?.source === Case.source.PW && !labOrder.user) // TODO: Make this use labOrder.is_initial_work attribute when it exists
      ) &&
      (!labOrder.slide_id ||
        sortedCase.slides.some((s) => s.id === labOrder.slide_id))
  );

  const filteredBlocks = blocks.filter((block) =>
    filteredLabOrders.some((lab_order) => {
      const slideBlockId = slides.find(
        (s) => s.id === lab_order.slide_id
      )?.block_obj_id;
      if (block.id === slideBlockId) return true;
      return lab_order.block_id === block.id;
    })
  );

  const onCancelOrder = (order: LabOrder) => {
    dispatch(cancelLabOrder(order));
  };

  return (
    <Sidebar isWide>
      <div
        className="StainOrderDialog Sidebar__contentContainer"
        data-testid="StainOrderDialog"
      >
        {!filteredLabOrders.length && !sortedCase.blocks?.length ? (
          <dl>
            <dt>There are no Blocks to place Orders on</dt>
          </dl>
        ) : (
          <>
            <dl>
              <dt>
                {filteredLabOrders.length ? (
                  <>Orders ({filteredLabOrders.length})</>
                ) : (
                  <>There are no active orders</>
                )}
              </dt>
              <dd>
                {filteredBlocks.map((block) => (
                  <StainOrderGroup
                    groupLabel={block.full_name}
                    group={filteredLabOrders.filter((labOrder) => {
                      const slideBlockId = slides.find(
                        (s) => s.id === labOrder.slide_id
                      )?.block_obj_id;
                      if (block.id === slideBlockId) return true;
                      return labOrder.block_id === block.id;
                    })}
                    onCancelOrder={onCancelOrder}
                    currentBlock={slideDetailObject.block_obj_id === block.id}
                    key={block.id}
                    hideStatusIcons={sortedCase?.source === Case.source.WPL}
                  />
                ))}
              </dd>
            </dl>
            {features?.stainOrdering && (
              <StainInputGroup
                slideDetail={slideDetailObject}
                stainCatalog={availableLabStains}
                setDialog={setDialog}
              />
            )}
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default StainOrderDialog;
